import { Action, State, StateContext, Store } from "@ngxs/store";
import { Injectable } from "@angular/core";
import { LocalizationService } from "src/app/services/localization/localization.service";
import { AlertService } from "src/app/shared/store/alert/alert.service";
import { patch, updateItem } from "@ngxs/store/operators";
import { MappingUtilsService } from "src/app/services/utils/mapping-utils.service";
import { AddInvoiceTemplateModel } from "../models/add-invoice-template.model";
import { InvoiceTemplateService } from "../services/invoice-template.service";
import { InvoiceTemplateActions } from "./invoice-template.actions";
import { InvoiceTemplateMap } from "../models/invoice-template-map.model";
import { InvoiceTemplate } from "../models/invoice-template.model";
import { EditInvoiceTemplateModel } from "../models/edit-invoice-template.model";
import { AddInvoiceTemplateConceptModel } from "../models/add-invoice-template-concept.model";
import { FormArray, FormGroup } from "@angular/forms";
import { InvoiceTemplateFilter } from "../models/invoice-template.filter.model";

export class InvoiceTemplateStateModel {
    public invoiceTemplates: InvoiceTemplate[];
    public invoiceTemplateMap: InvoiceTemplateMap[];
    public invoiceTemplateForm: {
      model: InvoiceTemplate
    };
    public invoiceTemplateAddForm: {
      model: AddInvoiceTemplateModel
    }
    public invoiceTemplateConceptAddForm: {
      model: AddInvoiceTemplateConceptModel
    }
    filters: {
      model: InvoiceTemplateFilter
    };
    public isLoading: boolean;
    public isListSmaller: boolean;
    public isSubmitted: boolean;
    public isEdited: boolean;
    public error: string;
  }

  const defaults = {
    invoiceTemplates: [],
    invoiceTemplateMap: [],
    invoiceTemplateForm: {
      model: null
    },
    invoiceTemplateAddForm: {
      model: null
    },
    invoiceTemplateConceptAddForm: {
      model: null
    },
    filters: {
      model: null
    },
    isLoading: false,
    isListSmaller: true,
    isSubmitted: false,
    isEdited: false,
    error: null
  };

  @State<InvoiceTemplateStateModel>({
    name: 'invoiceTemplate',
    defaults,
  })
  @Injectable()
  export class InvoiceTemplateState {
    constructor(
      private alertService: AlertService,
      private localizationService: LocalizationService,
      private invoiceTemplateService: InvoiceTemplateService,
      public store: Store
    ) {}

  @Action(InvoiceTemplateActions.GetInvoiceTemplates)
  getInvoiceTemplates (ctx: StateContext<InvoiceTemplateStateModel>, {user, exerciseId, filter }: InvoiceTemplateActions.GetInvoiceTemplates) {
    ctx.patchState({ isLoading: true });
    return this.invoiceTemplateService.getInvoiceTemplatesByFilter(user, exerciseId, filter)
      .subscribe({
        next: (response) =>
          ctx.dispatch(new InvoiceTemplateActions.GetInvoiceTemplatesSuccess(response)),
        error: (error) =>
          ctx.dispatch(new InvoiceTemplateActions.Error(error)),
      });
  }

  @Action(InvoiceTemplateActions.GetInvoiceTemplatesSuccess)
  getInvoiceTemplateSuccess(ctx: StateContext<InvoiceTemplateStateModel>, { invoiceTemplates }: InvoiceTemplateActions.GetInvoiceTemplatesSuccess) {
    ctx.patchState({
      invoiceTemplates: invoiceTemplates,
      isLoading: false,
    });
  }

  @Action(InvoiceTemplateActions.GetInvoiceTemplateDetails)
  getInvoiceTemplateDetails (ctx: StateContext<InvoiceTemplateStateModel>, { invoiceTemplateId }: InvoiceTemplateActions.GetInvoiceTemplateDetails) {
    ctx.patchState({ isLoading: true });
    return this.invoiceTemplateService.getInvoiceTemplateDetails(invoiceTemplateId)
      .subscribe({
        next: (response) =>
          ctx.dispatch(new InvoiceTemplateActions.GetInvoiceTemplateDetailsSuccess(response)),
        error: (error) =>
          ctx.dispatch(new InvoiceTemplateActions.Error(error)),
      });
  }

  @Action(InvoiceTemplateActions.GetInvoiceTemplateDetailsSuccess)
  getInvoiceTemplateDetailsSuccess (ctx: StateContext<InvoiceTemplateStateModel>, { invoiceTemplate }: InvoiceTemplateActions.GetInvoiceTemplateDetailsSuccess) {
    ctx.patchState({
      invoiceTemplateForm: { model: invoiceTemplate },
      isLoading: false
    });
  }

  @Action(InvoiceTemplateActions.AddInvoiceTemplate)
  addInvoiceTemplate(ctx: StateContext<InvoiceTemplateStateModel>, {user}: InvoiceTemplateActions.AddInvoiceTemplate) {
    var state = ctx.getState();
    var addInvoiceTemplate = state.invoiceTemplateAddForm.model;
    this.alertService.confirm(
    this.getTitle(),
    this.localizationService.translate('invoiceTemplates.invoiceTemplateAddConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('invoiceTemplates.invoiceTemplateAddConfirm')
    .replace('{0}', addInvoiceTemplate.name)
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        return this.invoiceTemplateService.createInvoiceTemplate(addInvoiceTemplate, user).subscribe({
          next: (newInvoiceTemplateId) => {
            ctx.dispatch(new InvoiceTemplateActions.AddInvoiceTemplateSuccess(addInvoiceTemplate, newInvoiceTemplateId))
          },
          error: (error) => {
            ctx.dispatch(new InvoiceTemplateActions.Error(error));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(InvoiceTemplateActions.AddInvoiceTemplateSuccess)
  addInvoiceTemplateSuccess(ctx: StateContext<InvoiceTemplateStateModel>, {addInvoiceTemplate, newInvoiceTemplateId}: InvoiceTemplateActions.AddInvoiceTemplateSuccess) {
    ctx.patchState({
      isLoading: false
    });
  }

  @Action(InvoiceTemplateActions.AddInvoiceTemplateConcept)
  addInvoiceTemplateConcept(ctx: StateContext<InvoiceTemplateStateModel>, {user}: InvoiceTemplateActions.AddInvoiceTemplateConcept) {
    var state = ctx.getState();
    var addInvoiceTemplateConcept = state.invoiceTemplateConceptAddForm.model;
    this.alertService.confirm(
    this.getTitle(),
    this.localizationService.translate('invoiceTemplateConcepts.invoiceTemplateConceptAddConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('invoiceTemplateConcepts.invoiceTemplateConceptAddConfirm')
    .replace('{0}', addInvoiceTemplateConcept.conceptName)
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        return this.invoiceTemplateService.createInvoiceTemplateConcept(addInvoiceTemplateConcept, user).subscribe({
          next: (newInvoiceTemplateId) => {
            ctx.dispatch(new InvoiceTemplateActions.AddInvoiceTemplateConceptSuccess(addInvoiceTemplateConcept, newInvoiceTemplateId))
          },
          error: (error) => {
            ctx.dispatch(new InvoiceTemplateActions.Error(error));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(InvoiceTemplateActions.AddInvoiceTemplateConceptSuccess)
  addInvoiceTemplateConceptSuccess(ctx: StateContext<InvoiceTemplateStateModel>, {addInvoiceTemplateConcept, newInvoiceTemplateId}: InvoiceTemplateActions.AddInvoiceTemplateConceptSuccess) {
    ctx.patchState({
      isLoading: false
    });
  }

  @Action(InvoiceTemplateActions.EditInvoiceTemplate)
  editInvoiceTemplate(ctx: StateContext<InvoiceTemplateStateModel>, {invoiceTemplateForm, user}: InvoiceTemplateActions.EditInvoiceTemplate) {
    var state = ctx.getState();
    var invoiceTemplate = state.invoiceTemplateForm.model;
    this.alertService.confirm(
      this.getTitle(),
      this.localizationService.translate('invoiceTemplates.invoiceTemplateEditConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('invoiceTemplates.invoiceTemplateEditConfirm')
      .replace('{0}', invoiceTemplate.name)
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        var editInvoiceTemplate = this.getEditInvoiceTemplate(invoiceTemplateForm);
        return this.invoiceTemplateService.updateInvoiceTemplate(editInvoiceTemplate, user).subscribe({
          next: () => ctx.dispatch(new InvoiceTemplateActions.EditInvoiceTemplateSuccess(invoiceTemplate)),
          error: (error) => {
            ctx.dispatch(new InvoiceTemplateActions.Error(error));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(InvoiceTemplateActions.EditInvoiceTemplateSuccess)
  editInvoiceTemplateSuccess(ctx: StateContext<InvoiceTemplateStateModel>, {editInvoiceTemplate}: InvoiceTemplateActions.EditInvoiceTemplateSuccess) {
    ctx.setState(
      patch({
        isLoading: false,
        invoiceTemplates: updateItem<InvoiceTemplate>(
          (invoiceTemplate) => invoiceTemplate.id === editInvoiceTemplate.id,
          patch({
            ...editInvoiceTemplate
          })
        ),
      })
    );
  }

  @Action(InvoiceTemplateActions.Error)
  onErrorOcurred(ctx: StateContext<InvoiceTemplateStateModel>, { error }: InvoiceTemplateActions.Error) {
    ctx.patchState({
      error: error,
      isLoading: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  @Action(InvoiceTemplateActions.SetInvoiceTemplatesDefaultState)
  setInvoiceTemplatesDefaultState(ctx: StateContext<InvoiceTemplateStateModel>, {}: InvoiceTemplateActions.SetInvoiceTemplatesDefaultState) {
    ctx.patchState(
      defaults
    );
  }

  @Action(InvoiceTemplateActions.SetInvoiceTemplateAddFormDefaultState)
  setInvoiceTemplatesAddFormDefaultState(ctx: StateContext<InvoiceTemplateStateModel>, {}: InvoiceTemplateActions.SetInvoiceTemplateAddFormDefaultState) {
    ctx.setState(
      patch({
        invoiceTemplateAddForm: patch({
          model: null
        }),
      })
    );
  }

  @Action(InvoiceTemplateActions.SetInvoiceTemplateConceptAddFormDefaultState)
  setInvoiceTemplatesConceptAddFormDefaultState(ctx: StateContext<InvoiceTemplateStateModel>, {}: InvoiceTemplateActions.SetInvoiceTemplateConceptAddFormDefaultState) {
    ctx.setState(
      patch({
        invoiceTemplateConceptAddForm: patch({
          model: null
        }),
      })
    );
  }

  @Action(InvoiceTemplateActions.SetInvoiceTemplateFormDefaultState)
  setInvoiceTemplatesFormDefaultState(ctx: StateContext<InvoiceTemplateStateModel>, {}: InvoiceTemplateActions.SetInvoiceTemplateFormDefaultState) {
    ctx.setState(
      patch({
        invoiceTemplateForm: patch({
          model: null
        }),
      })
    );
  }

  @Action(InvoiceTemplateActions.SetInvoiceTemplateFilterDefaultState)
  setInvoiceTemplateFilterDefaultState(ctx: StateContext<InvoiceTemplateStateModel>, {}: InvoiceTemplateActions.SetInvoiceTemplateFilterDefaultState) {
    ctx.setState(
      patch({
        filters: patch({
          model: null
        }),
      })
    );
  }

  @Action(InvoiceTemplateActions.SetIsSubmitted)
  setIsSubmitted(
    ctx: StateContext<InvoiceTemplateStateModel>,
    { isSubmitted }: InvoiceTemplateActions.SetIsSubmitted
  ) {
    ctx.patchState({
      isSubmitted,
    });
  }

  @Action(InvoiceTemplateActions.SetIsEdited)
  setIsEdited(
    ctx: StateContext<InvoiceTemplateStateModel>,
    { isEdited }: InvoiceTemplateActions.SetIsEdited
  ) {
    ctx.patchState({
      isEdited,
    });
  }

  private getEditInvoiceTemplate(invoiceTemplate: FormGroup): EditInvoiceTemplateModel {
    let editInvoiceTemplate: EditInvoiceTemplateModel = {
      id: invoiceTemplate.get('id').value,
      name: invoiceTemplate.get('name').value,
      isActive: invoiceTemplate.get('isActive').value,
      zoneId: invoiceTemplate.get('zoneId').value,
      ceco: invoiceTemplate.get('ceco').value,
      concepts: this.createInvoiceTemplateConcepts(invoiceTemplate.get('invoiceTemplateConcepts') as FormArray<FormGroup>)
    }

    return editInvoiceTemplate;
  }

  private createInvoiceTemplateConcepts(invoiceTemplateConcepts: FormArray<FormGroup>) {
    let updatedInvoiceTemplateConcepts = [];
    invoiceTemplateConcepts.controls.filter(x => x.dirty).forEach(invoiceTemplateConcept => {
      let updatedIntervalPrice = {
        id: invoiceTemplateConcept.get('id').value,
        name: invoiceTemplateConcept.get('name').value,
        average: invoiceTemplateConcept.get('average').value,
        priceUnit: invoiceTemplateConcept.get('priceUnit').value,
        invoiceAccountId: invoiceTemplateConcept.get('invoiceAccountId').value,
        isActive: invoiceTemplateConcept.get('isActive').value,
      };
      updatedInvoiceTemplateConcepts.push(updatedIntervalPrice);
    });

    return updatedInvoiceTemplateConcepts;
  }

  private createInvoiceTemplate(addInvoiceTemplateModel: AddInvoiceTemplateModel, newInvoiceTemplateId: string): InvoiceTemplate {
    return {
      id: newInvoiceTemplateId,
      name: addInvoiceTemplateModel.name,
      isActive: true,
      ceco: addInvoiceTemplateModel.ceco,
      zoneId: addInvoiceTemplateModel.zoneId,
      concepts: []
    }
  }

  private getTitle() {
    return this.localizationService.translate('invoiceTemplates.title');
  }
}
